import React, { InputHTMLAttributes, useCallback } from 'react';
import { useLocaleDecimalInput } from '../../hooks/useLocaleDecimalInput';

export interface NativeDecimalNumberInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'defaultValue'
> {
  value: number | string | null | undefined;
  onChange: (value: number | null) => void;
  decimalPlaces?: number;
  min?: number;
  max?: number;
}

const NativeDecimalNumberInput = React.forwardRef<HTMLInputElement, NativeDecimalNumberInputProps>(
  ({ value, onChange, decimalPlaces = 2, min, max, onFocus, onBlur, ...inputProps }, ref) => {
    const {
      displayValue,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onChange: handleChange,
    } = useLocaleDecimalInput({
      value,
      onChange,
      decimalPlaces,
      min,
      max,
    });

    const handleInputFocus = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        handleFocus();
        onFocus?.(event);
      },
      [handleFocus, onFocus],
    );

    const handleInputBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        handleBlur();
        onBlur?.(event);
      },
      [handleBlur, onBlur],
    );

    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        handleChange(event.target.value);
      },
      [handleChange],
    );

    return (
      <input
        {...inputProps}
        ref={ref}
        type="text"
        inputMode="decimal"
        autoComplete={inputProps.autoComplete ?? 'off'}
        value={displayValue}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onChange={handleInputChange}
      />
    );
  },
);

NativeDecimalNumberInput.displayName = 'NativeDecimalNumberInput';

export default NativeDecimalNumberInput;
